import type { AxisDecision, CoverageExplanation, TupleExplanation } from '@atlas/types'
import { Chip } from '@atlas/ui-primitives'
import { ASSERTION_HINT } from '@/components/classification/ChipSet'

export interface ExplanationViewProps {
	explanation: CoverageExplanation
}

/**
 * Renders an explain result: the verdict, then every assertion with the per-axis
 * reason it matched or failed.
 *
 * The API returns taxonomy internals as raw data on purpose — no human-readable
 * message — so translating an outcome into a sentence happens HERE, at the surface
 * that presents it. That is what keeps the model out of the API contract.
 *
 * Presentational + storied: renders from props, never fetches.
 */
export function ExplanationView({ explanation }: ExplanationViewProps) {
	const { verdict, mode, matchingTupleIds, tuples } = explanation
	// The heading and the cards below must agree on how an assertion is numbered
	// (position, not the raw tuple id — see ASSERTION_HINT/TupleChipSet), so matched
	// ids are mapped to their position in `tuples` rather than rendered as-is.
	const matchingPositions = tuples
		.map((tuple, index) => (matchingTupleIds.includes(tuple.tupleId) ? index + 1 : null))
		.filter((position): position is number => position !== null)

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-3">
				<Chip variant={verdict ? 'success' : 'danger'} size="md">
					{verdict ? 'INCLUDED' : 'NOT INCLUDED'}
				</Chip>
				<p className="text-sm text-ds-text-muted">
					{verdict
						? `Matched by ${matchingPositions.length === 1 ? 'assertion' : 'assertions'} ${matchingPositions.join(', ')}`
						: 'No single assertion satisfies this context'}
					<span className="ml-2 text-ds-text-subtle">· {mode} mode</span>
				</p>
			</div>

			<div className="space-y-3">
				{tuples.map((tuple, index) => (
					<AssertionResult key={tuple.tupleId} tuple={tuple} position={index + 1} />
				))}
			</div>
		</div>
	)
}

function AssertionResult({ tuple, position }: { tuple: TupleExplanation; position: number }) {
	return (
		<div
			className={`space-y-2 rounded-ds-md border p-3 ${
				tuple.matched ? 'border-ds-success bg-ds-surface' : 'border-ds-border bg-ds-surface-subtle'
			}`}
		>
			<div className="flex items-center justify-between">
				<p className="text-ds-2xs font-medium uppercase tracking-widest text-ds-text-subtle" title={ASSERTION_HINT}>
					Assertion {position}
				</p>
				<span className={`text-xs font-medium ${tuple.matched ? 'text-ds-success' : 'text-ds-text-subtle'}`}>
					{tuple.matched ? 'matched' : `failed on ${tuple.failedOnAxis}`}
				</span>
			</div>
			{tuple.axes.length === 0 ? (
				<p className="text-xs text-ds-text-subtle">
					Constrains nothing the context asks about, so it imposes no restriction.
				</p>
			) : (
				<ul className="space-y-1">
					{tuple.axes.map((axis) => (
						<li key={axis.axisCode} className="flex items-start gap-2 text-xs">
							<span className="min-w-24 font-mono text-ds-text-muted">{axis.axisCode}</span>
							<span className="text-ds-text">{axisReason(axis)}</span>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

/**
 * One axis' decision as a sentence. The four outcomes are genuinely different
 * diagnoses, and collapsing them is what makes a match hard to explain:
 * `undeclared-by-context` means "you didn't ask about this", `not-covered` means
 * "you asked and the answer was no".
 */
function axisReason(axis: AxisDecision): string {
	switch (axis.outcome) {
		case 'covered':
			return axis.objectValue === axis.contextValue
				? `matches "${axis.contextValue}"`
				: `"${axis.objectValue}" covers "${axis.contextValue}" through the hierarchy`
		case 'unconstrained':
			return 'this assertion says nothing about this axis, so it imposes nothing'
		case 'undeclared-by-context':
			return `this assertion requires ${describe(axis.objectValues)}, but the context never mentions this axis`
		case 'not-covered':
			return `the context asks for ${describe(axis.contextValues)}, which ${describe(axis.objectValues)} does not cover`
	}
}

const describe = (values: string[]): string =>
	values.length === 0 ? 'nothing' : values.map((v) => `"${v}"`).join(' or ')
